import { accountMessages } from "./accountMessages";
import { adminAccessMessages } from "./adminAccessMessages";
import { adminAuditMessages } from "./adminAuditMessages";
import { adminConsoleMessages } from "./adminConsoleMessages";
import { adminLogsMessages } from "./adminLogsMessages";
import { adminShellMessages } from "./adminShellMessages";
import { adminUsersMessages } from "./adminUsersMessages";
import { billingMessages } from "./billingMessages";
import { introMessages } from "./introMessages";

/**
 * Interface strings only. User content never passes through here — it stays
 * untrusted text and is rendered verbatim.
 *
 * Russian copy is transcribed from the Mioscape mockups; English is the
 * fallback locale and matches the wording already in the code.
 */
export const LOCALES = ["ru", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export const FALLBACK_LOCALE: Locale = "en";

export const messages = {
  ...adminUsersMessages,
  ...adminLogsMessages,
  ...adminConsoleMessages,
  ...adminShellMessages,
  ...adminAuditMessages,
  ...adminAccessMessages,
  ...introMessages,
  ...billingMessages,
  ...accountMessages,
  // Shell
  "app.name": { ru: "Mioscape", en: "Mioscape" },
  "app.workspace": { ru: "Личный Workspace", en: "Personal Workspace" },
  "app.administration": {
    ru: "Администрирование платформы",
    en: "Platform Administration",
  },
  "app.close": { ru: "Закрыть", en: "Close" },
  "app.closeAria": { ru: "Закрыть Mini App", en: "Close Mini App" },
  "app.back": { ru: "Назад", en: "Back" },
  "nav.home": { ru: "Главная", en: "Home" },
  "nav.profile": { ru: "Профиль", en: "Profile" },
  "nav.sections": { ru: "Разделы", en: "Sections" },
  "nav.assistant": { ru: "Ассистент", en: "Assistant" },
  "nav.sectionMenu": { ru: "Меню раздела", en: "Section menu" },
  "nav.aria": { ru: "Навигация по Workspace", en: "Workspace navigation" },

  // Startup sequence
  "startup.hostInitializing": { ru: "Запуск", en: "Starting up" },
  "startup.authenticating": { ru: "Проверка сессии", en: "Verifying session" },
  "startup.resolvingTarget": {
    ru: "Определение маршрута",
    en: "Resolving route",
  },
  "startup.loadingRoute": { ru: "Загрузка Workspace", en: "Loading Workspace" },

  // Recovery states
  "recovery.host_unsupported.title": {
    ru: "Откройте Mioscape в Telegram",
    en: "Open Mioscape in Telegram",
  },
  "recovery.host_unsupported.body": {
    ru: "Mini App работает только внутри Telegram: здесь нет данных о вашем аккаунте. Откройте бота и нажмите «Открыть приложение».",
    en: "The Mini App only runs inside Telegram: there is no account data here. Open the bot and tap “Open app”.",
  },
  "recovery.host_unsupported.action": {
    ru: "Открыть в Telegram",
    en: "Open in Telegram",
  },
  "recovery.session_rejected.title": {
    ru: "Сессия истекла",
    en: "Session expired",
  },
  "recovery.session_rejected.body": {
    ru: "Данные входа устарели. Закройте приложение и откройте его заново из чата с ботом.",
    en: "Your sign-in data is out of date. Close the app and open it again from the bot chat.",
  },
  "recovery.session_rejected.action": { ru: "Закрыть", en: "Close" },
  "recovery.auth_expired.title": {
    ru: "Сессия истекла",
    en: "Session expired",
  },
  "recovery.auth_expired.body": {
    ru: "Данные входа устарели, пока приложение было закрыто. Откройте главную — вход обновится автоматически.",
    en: "Your sign-in went stale while the app was closed. Open the home screen and it renews automatically.",
  },
  "recovery.auth_expired.action": { ru: "Открыть главную", en: "Open home" },
  "recovery.nav_token_expired.title": {
    ru: "Ссылка устарела",
    en: "This link has expired",
  },
  "recovery.nav_token_expired.body": {
    ru: "Ссылка на этот экран действовала ограниченное время. Откройте нужный раздел с главной — данные на месте.",
    en: "The link to this screen was only valid for a while. Open the section from home — your data is intact.",
  },
  "recovery.nav_token_expired.action": {
    ru: "Открыть главную",
    en: "Open home",
  },
  "recovery.offline.title": { ru: "Нет сети", en: "No connection" },
  "recovery.offline.body": {
    ru: "Mioscape не смог связаться с сервером. Проверьте соединение и повторите.",
    en: "Mioscape could not reach the server. Check your connection and try again.",
  },
  "recovery.offline.action": { ru: "Повторить", en: "Retry" },
  "recovery.backend_error.title": {
    ru: "Сервер недоступен",
    en: "The server is unavailable",
  },
  "recovery.backend_error.body": {
    ru: "Мы уже знаем о проблеме и чиним её. Ваши записи в безопасности, ничего не потеряно.",
    en: "We already know about the problem and are fixing it. Your records are safe; nothing is lost.",
  },
  "recovery.backend_error.action": { ru: "Повторить", en: "Retry" },
  "recovery.render_error.title": {
    ru: "Что-то пошло не так",
    en: "Something went wrong",
  },
  "recovery.render_error.body": {
    ru: "Экран не удалось отрисовать. Перезапустите приложение — данные не пострадали.",
    en: "The screen could not be drawn. Restart the app — your data is unharmed.",
  },
  "recovery.render_error.action": { ru: "Перезапустить", en: "Restart" },
  "error.notFound.title": { ru: "Не найдено", en: "Not found" },
  "error.notFound.action": { ru: "В Workspace", en: "Back to Workspace" },
  "error.retry": { ru: "Повторить", en: "Retry" },
  "error.correlationId": { ru: "ID", en: "ID" },
  "error.reference": { ru: "Код", en: "Code" },
  "error.reference.copy": { ru: "Скопировать код", en: "Copy code" },
  "error.reference.copied": { ru: "Скопировано", en: "Copied" },
  "error.noView": { ru: "Нет доступных view.", en: "No view is available." },

  // Empty states
  "list.all": { ru: "Все", en: "All" },
  "list.more": { ru: "Показать ещё", en: "Show more" },
  "list.clearFilter": { ru: "Снять фильтр", en: "Clear filter" },
  "list.filteredEmpty.title": {
    ru: "Под фильтр ничего не подходит",
    en: "Nothing matches the filter",
  },
  "list.filteredEmpty.body": {
    ru: "Снимите фильтр или выберите другой.",
    en: "Clear the filter or pick another one.",
  },
  "empty.title": { ru: "Пока пусто", en: "Nothing here yet" },
  "empty.body": { ru: "Записи появятся здесь", en: "Records will appear here" },
  "empty.records": { ru: "Записей пока нет", en: "No records yet" },

  // Unavailable commands — never a bare disabled button
  "unavailable.prefix": { ru: "Недоступно", en: "Unavailable" },

  // Workspace Home

  // Records
  "record.new": { ru: "Новая запись", en: "New record" },
  "record.edit": { ru: "Редактирование", en: "Edit record" },
  "record.save": { ru: "Сохранить", en: "Save" },
  "record.saving": { ru: "Сохранение…", en: "Saving…" },
  "record.cancel": { ru: "Отмена", en: "Cancel" },
  "record.delete": { ru: "Удалить запись", en: "Delete record" },
  "reminders.title": { ru: "Название", en: "Title" },
  "reminders.time": { ru: "Дата и время", en: "Date and time" },
  "reminders.repeat": { ru: "Повтор", en: "Repeat" },
  "reminders.today": { ru: "Сегодня", en: "Today" },
  "reminders.week": { ru: "Неделя", en: "Week" },
  "reminders.all": { ru: "Все", en: "All" },
  "reminders.filters": { ru: "Период напоминаний", en: "Reminder period" },
  "reminders.empty": {
    ru: "В этом периоде нет напоминаний",
    en: "No reminders in this period",
  },
  "reminders.add": { ru: "Добавить напоминание", en: "Add reminder" },
  "reminders.overdue": { ru: "просрочено", en: "overdue" },
  "reminders.sent": { ru: "Отправлено", en: "Sent" },
  "reminders.when": { ru: "Когда", en: "When" },
  "reminders.edit": { ru: "Изменить", en: "Edit" },
  "reminders.delete": { ru: "Удалить", en: "Delete" },
  "reminders.more": { ru: "Ещё", en: "More" },
  "reminders.reschedule": { ru: "Перенести", en: "Reschedule" },
  "reminders.rescheduleTitle": {
    ru: "Перенести напоминание",
    en: "Reschedule reminder",
  },
  "reminders.newTime": { ru: "Новое время", en: "New time" },
  "reminders.timezone": { ru: "Часовой пояс", en: "Time zone" },
  "reminders.pastTime": {
    ru: "Укажите время позже текущего",
    en: "Pick a time later than now",
  },
  "reminders.inHour": { ru: "Через час", en: "In an hour" },
  "reminders.evening": { ru: "Вечером", en: "This evening" },
  "reminders.tomorrow": { ru: "Завтра", en: "Tomorrow" },
  "reminders.stop": { ru: "Остановить повтор", en: "Stop repeating" },
  "reminders.stopConfirm.title": {
    ru: "Остановить повтор?",
    en: "Stop repeating?",
  },
  "reminders.stopConfirm.body": {
    ru: "Напоминание перестанет приходить. Возобновить повтор можно в любой момент.",
    en: "The reminder will stop arriving. You can resume it at any time.",
  },
  "reminders.stopConfirm.confirm": { ru: "Остановить", en: "Stop" },
  "reminders.resume": { ru: "Начать повтор", en: "Resume repeating" },
  "reminders.missed.title": { ru: "Просрочено", en: "Overdue" },
  "reminders.missed.body": {
    ru: "Бот не смог отправить его вовремя. Перенесите или удалите.",
    en: "The bot could not send it on time. Reschedule or delete it.",
  },
  "reminders.stopped.title": { ru: "Повтор остановлен", en: "Repeat stopped" },
  "reminders.stopped.body": {
    ru: "Напоминание не приходит, пока повтор не возобновлён.",
    en: "The reminder does not arrive until repeating is resumed.",
  },
  "reminders.deleteConfirm.title": {
    ru: "Удалить напоминание?",
    en: "Delete reminder?",
  },
  "reminders.deleteConfirm.body": {
    ru: "Напоминание будет удалено без возможности восстановления.",
    en: "The reminder will be deleted and cannot be recovered.",
  },
  "reminders.once": { ru: "Один раз", en: "Once" },
  "reminders.daily": { ru: "Каждый день", en: "Daily" },
  "reminders.weekly": { ru: "Каждую неделю", en: "Weekly" },
  "reminders.monthly": { ru: "Каждый месяц", en: "Monthly" },
  "nav.system": { ru: "Системный раздел", en: "System section" },
  "record.error": {
    ru: "Операция не выполнена. Данные сохранены в форме; проверьте связи и обновите запись перед повтором.",
    en: "The operation failed. Your values are preserved; check references and reload the record before retrying.",
  },
  "record.required": { ru: "обязательно", en: "required" },
  "record.deleteConfirm.title": {
    ru: "Удалить запись?",
    en: "Delete record?",
  },
  "record.deleteConfirm.body": {
    ru: "Запись будет удалена без возможности восстановления.",
    en: "The record will be deleted and cannot be recovered.",
  },
  "record.deleteConfirm.confirm": { ru: "Удалить", en: "Delete" },

  // Revision conflict — no automatic last-write-wins
  "conflict.title": { ru: "Конфликт изменений", en: "Revision conflict" },
  "conflict.body": {
    ru: "Пока вы редактировали, запись изменилась на сервере. Автослияния нет — выберите вручную.",
    en: "The record changed on the server while you were editing. There is no automatic merge — choose manually.",
  },
  "conflict.mine": { ru: "Ваше", en: "Yours" },
  "conflict.server": { ru: "Сервер", en: "Server" },
  "conflict.unchanged": { ru: "без изменений", en: "unchanged" },
  "conflict.emptyValue": { ru: "пустое значение", en: "empty value" },
  "conflict.reload": { ru: "Перезагрузить", en: "Reload" },
  "conflict.keepMine": { ru: "Вернуться к своим", en: "Keep mine" },
  "conflict.discardMine": { ru: "Отбросить моё", en: "Discard mine" },
  "conflict.revision": { ru: "Ревизия сервера", en: "Server revision" },

  // Draft preview
  "draft.title": { ru: "Черновик", en: "Draft preview" },
  "draft.banner": {
    ru: "Изменения записей не сохраняются.",
    en: "Record edits are not saved.",
  },
  "draft.interface.title": {
    ru: "Изменение интерфейса — записи не меняются",
    en: "Interface change — records are not affected",
  },
  "draft.interface.body": {
    ru: "Опубликуйте черновик, чтобы применить изменения, или отмените их.",
    en: "Publish the draft to apply the changes, or discard them.",
  },
  "draft.interface.discard": {
    ru: "Отменить изменения",
    en: "Discard changes",
  },
  "draft.interface.confirm.title": {
    ru: "Отменить изменения?",
    en: "Discard changes?",
  },
  "draft.interface.confirm.body": {
    ru: "Черновик будет удалён. Опубликованный интерфейс и записи останутся как есть.",
    en: "The draft will be deleted. The published interface and records stay as they are.",
  },
  "draft.interface.discarded": {
    ru: "Изменения отменены",
    en: "Changes discarded",
  },
  "draft.interface.failed": {
    ru: "Не удалось отменить изменения",
    en: "Could not discard the changes",
  },
  "draft.stale": {
    ru: "Черновик изменился, пока вы смотрели. Мы обновили его — проверьте и опубликуйте снова.",
    en: "The draft changed while you were viewing it. We reloaded it — review it and publish again.",
  },
  "draft.revision": { ru: "ревизия", en: "revision" },
  /** Sentence-initial form, for the draft banner. */
  "draft.revisionLabel": { ru: "Ревизия", en: "Revision" },
  "draft.reset": {
    ru: "Сбросить локальные изменения",
    en: "Reset local changes",
  },
  "draft.resetShort": { ru: "Сбросить", en: "Reset" },
  "draft.publish": { ru: "Опубликовать", en: "Publish" },
  "draft.publishConfirm.title": {
    ru: "Опубликовать черновик?",
    en: "Publish draft?",
  },
  "draft.publishConfirm.body": {
    ru: "После публикации раздел начнёт работать по новой схеме.",
    en: "Once published, the section switches to the new schema.",
  },
  "draft.incompatible.title": {
    ru: "Изменения удалят данные",
    en: "These changes would remove data",
  },
  "draft.incompatible.body": {
    ru: "Правки схемы несовместимы с уже сохранёнными записями, поэтому публикация заблокирована. Попросите ассистента изменить черновик.",
    en: "The schema changes do not fit the records already saved, so publishing is blocked. Ask the assistant to change the draft.",
  },
  "draft.change.field_removed": { ru: "Удалено поле", en: "Removed field" },
  "draft.change.field_type_changed": {
    ru: "Изменён тип поля",
    en: "Changed type of field",
  },
  "draft.change.collection_removed": {
    ru: "Удалена коллекция",
    en: "Removed collection",
  },
  "draft.change.required_field_without_default": {
    ru: "Новое обязательное поле без значения по умолчанию",
    en: "New required field without a default",
  },
  "draft.change.other": {
    ru: "Несовместимое изменение",
    en: "Incompatible change",
  },
  "draft.changes.title": {
    ru: "Что изменится",
    en: "What changes",
  },
  "draft.changes.relative": {
    ru: "Относительно версии",
    en: "Compared with version",
  },
  "draft.changes.new": {
    ru: "Новый раздел",
    en: "New section",
  },
  "draft.changes.newBody": {
    ru: "Раздел ещё не опубликован: сравнивать пока не с чем.",
    en: "The section is not published yet, so there is nothing to compare with.",
  },
  "draft.changes.none": {
    ru: "Изменений нет",
    en: "No changes",
  },
  "draft.changes.group.fields": {
    ru: "Поля",
    en: "Fields",
  },
  "draft.changes.group.views": {
    ru: "Экраны",
    en: "Screens",
  },
  "draft.changes.group.print": {
    ru: "Печать",
    en: "Print",
  },
  "draft.changes.field_added": {
    ru: "Добавлено поле",
    en: "Added field",
  },
  "draft.changes.field_hidden": {
    ru: "Скрыто поле (данные сохранятся)",
    en: "Hidden field (data is kept)",
  },
  "draft.changes.field_shown": {
    ru: "Показано поле",
    en: "Shown field",
  },
  "draft.changes.label_changed": {
    ru: "Переименовано",
    en: "Renamed",
  },
  "draft.changes.default_changed": {
    ru: "Изменено значение по умолчанию у поля",
    en: "Changed the default of field",
  },
  "draft.changes.required_on": {
    ru: "Поле стало обязательным",
    en: "Field is now required",
  },
  "draft.changes.required_off": {
    ru: "Поле стало необязательным",
    en: "Field is no longer required",
  },
  "draft.changes.option_added": {
    ru: "Добавлен вариант",
    en: "Added option",
  },
  "draft.changes.option_color_changed": {
    ru: "Изменён цвет варианта",
    en: "Changed colour of option",
  },
  "draft.changes.collection_added": {
    ru: "Новая таблица",
    en: "New table",
  },
  "draft.changes.view_added": {
    ru: "Добавлен экран",
    en: "Added screen",
  },
  "draft.changes.layout_changed": {
    ru: "Изменена раскладка",
    en: "Changed layout of",
  },
  "draft.changes.order_changed": {
    ru: "Изменён порядок",
    en: "Changed order in",
  },
  "draft.changes.list_config_changed": {
    ru: "Изменены настройки списка на экране",
    en: "Changed list settings on",
  },
  "draft.changes.control_added": {
    ru: "Добавлена кнопка",
    en: "Added button",
  },
  "draft.changes.control_removed": {
    ru: "Убрана кнопка",
    en: "Removed button",
  },
  "draft.changes.print_form_added": {
    ru: "Добавлена печатная форма",
    en: "Added print form",
  },
  "draft.changes.print_form_changed": {
    ru: "Изменена печатная форма",
    en: "Changed print form",
  },
  "draft.changes.primary_field_changed": {
    ru: "Изменено основное поле таблицы",
    en: "Changed the main field of",
  },
  "draft.changes.other_changed": {
    ru: "Другие изменения",
    en: "Other changes",
  },
  "draft.changes.listKey.group_by": {
    ru: "группировка",
    en: "grouping",
  },
  "draft.changes.listKey.sort_by": {
    ru: "сортировка",
    en: "sorting",
  },
  "draft.changes.listKey.sort_dir": {
    ru: "направление сортировки",
    en: "sort direction",
  },
  "draft.changes.listKey.badge_field_id": {
    ru: "метка",
    en: "badge",
  },
  "draft.changes.listKey.subtitle_field_id": {
    ru: "подзаголовок",
    en: "subtitle",
  },
  "draft.changes.listKey.empty_text": {
    ru: "текст пустого списка",
    en: "empty-list text",
  },
  "draft.changes.listKey.filters": {
    ru: "фильтры",
    en: "filters",
  },
  "draft.backfill.title": {
    ru: "Существующие записи получат значение по умолчанию",
    en: "Existing records will get a default value",
  },
  "draft.backfill.field": {
    ru: "Поле",
    en: "Field",
  },
  "draft.backfill.records": {
    ru: "записей",
    en: "records",
  },
  "draft.backfill.value": {
    ru: "значение",
    en: "value",
  },
  "draft.staleBase.title": {
    ru: "Раздел изменился после создания черновика",
    en: "The section changed after this draft was made",
  },
  "draft.staleBase.body": {
    ru: "Опубликовать его нельзя. Удалите черновик и попросите ассистента внести правки заново.",
    en: "It cannot be published. Delete the draft and ask the assistant to make the changes again.",
  },
  "draft.staleBase.discard": {
    ru: "Удалить черновик",
    en: "Delete draft",
  },
  "draft.publishConfirm.count": {
    ru: "Изменений в черновике",
    en: "Changes in the draft",
  },
  "draft.publishConfirm.backfill": {
    ru: "Значения по умолчанию будут записаны в существующие записи.",
    en: "Default values will be written into existing records.",
  },
  "draft.published": {
    ru: "Опубликовано, версия",
    en: "Published, version",
  },
  "draft.noChanges": {
    ru: "Черновик ничем не отличается от опубликованного раздела.",
    en: "The draft is the same as the published section.",
  },
  "draft.backfillTooLarge": {
    ru: "Слишком много записей для заполнения значением по умолчанию. Публикация отменена, данные не тронуты.",
    en: "Too many records to fill with a default. Publishing was cancelled and no data was touched.",
  },
  "draft.publishFailed": {
    ru: "Не удалось опубликовать",
    en: "Publishing failed",
  },
  "history.open": {
    ru: "История версий",
    en: "Version history",
  },
  "history.title": {
    ru: "История версий",
    en: "Version history",
  },
  "history.hint": {
    ru: "Откат создаёт черновик на основе выбранной версии. Опубликованный, он станет новой версией: история не стирается, записи сохраняются.",
    en: "Restoring creates a draft from the chosen version. Published, it becomes a new version: history is never erased and records are kept.",
  },
  "history.version": {
    ru: "Версия",
    en: "Version",
  },
  "history.current": {
    ru: "Текущая",
    en: "Current",
  },
  "history.restore": {
    ru: "Восстановить",
    en: "Restore",
  },
  "history.restoreVersion": {
    ru: "Восстановить версию",
    en: "Restore version",
  },
  "history.loadFailed": {
    ru: "Не удалось загрузить историю версий.",
    en: "Could not load the version history.",
  },
  "history.draftExists": {
    ru: "У раздела уже есть черновик. Откройте его или удалите, прежде чем восстанавливать версию.",
    en: "This section already has a draft. Open or delete it before restoring a version.",
  },
  "history.notRestorable": {
    ru: "Эту версию нельзя восстановить: она несовместима с текущими правилами.",
    en: "This version cannot be restored: it no longer fits the current rules.",
  },
  "history.noChanges": {
    ru: "Восстановление этой версии ничего не изменит.",
    en: "Restoring this version would change nothing.",
  },
  "history.failed": {
    ru: "Не удалось создать черновик. Попробуйте ещё раз.",
    en: "Could not create the draft. Please try again.",
  },
  "keywords.open": { ru: "Ключевые слова", en: "Keywords" },
  "keywords.title": { ru: "Ключевые слова", en: "Keywords" },
  "keywords.hint": {
    ru: "По этим словам ассистент понимает, о каком разделе речь. Изменения попадают в черновик: его нужно просмотреть и опубликовать.",
    en: "The assistant uses these words to tell which section a request is about. Changes go into a draft that you review and publish.",
  },
  "keywords.loadFailed": {
    ru: "Не удалось загрузить ключевые слова.",
    en: "Could not load the keywords.",
  },
  "keywords.draftShown": {
    ru: "Показаны слова из черновика раздела.",
    en: "Showing the keywords of the section's draft.",
  },
  "keywords.source.labels": { ru: "из названий", en: "from names" },
  "keywords.source.suggested": { ru: "предложено", en: "suggested" },
  "keywords.source.owner": { ru: "добавлено вами", en: "added by you" },
  "keywords.off": { ru: "отключено", en: "off" },
  "keywords.switchOff": { ru: "Отключить слово", en: "Switch off the word" },
  "keywords.switchOn": { ru: "Вернуть слово", en: "Bring the word back" },
  "keywords.remove": { ru: "Убрать слово", en: "Remove the word" },
  "keywords.phrases": { ru: "Типичные запросы", en: "Typical requests" },
  "keywords.addTo": { ru: "Куда добавить", en: "Add to" },
  "keywords.addPhrase": { ru: "Типичный запрос", en: "Typical request" },
  "keywords.word": { ru: "Слово", en: "Word" },
  "keywords.add": { ru: "Добавить", en: "Add" },
  "keywords.saved": {
    ru: "Изменение сохранено в черновик.",
    en: "The change was saved to the draft.",
  },
  "keywords.review": { ru: "Просмотреть черновик", en: "Review the draft" },
  "keywords.invalid": {
    ru: "Слово должно быть коротким: буквы, цифры, пробелы и дефисы, без ссылок и длинных номеров.",
    en: "A word must be short: letters, digits, spaces and hyphens, with no links or long numbers.",
  },
  "keywords.full": {
    ru: "Для этого места уже много слов. Уберите ненужные.",
    en: "This place already has many words. Remove some first.",
  },
  "keywords.failed": {
    ru: "Не удалось сохранить. Попробуйте ещё раз.",
    en: "Could not save. Please try again.",
  },
  "keywords.conflict": {
    ru: "Черновик раздела изменился или устарел. Откройте его и повторите.",
    en: "The section's draft changed or is out of date. Open it and try again.",
  },
  // Assistant Job
  "assistantJob.title": { ru: "Запрос ассистенту", en: "Assistant request" },
  "assistantJob.unavailable": {
    ru: "Этот запрос доступен только в подтверждённой сессии Telegram.",
    en: "This request is only available in an authenticated Telegram session.",
  },
  "assistantJob.state": { ru: "Статус", en: "Status" },
  "assistantJob.updated": { ru: "Обновлён", en: "Updated" },
  "assistantJob.cancel": { ru: "Отменить запрос", en: "Cancel request" },
  "assistantJob.cancelTitle": {
    ru: "Отменить запрос ассистенту?",
    en: "Cancel the Assistant request?",
  },
  "assistantJob.cancelBody": {
    ru: "Ассистент остановит обработку при ближайшей безопасной точке.",
    en: "The Assistant will stop at the next safe point.",
  },
  "assistantJob.cancelConfirm": { ru: "Да, отменить", en: "Yes, cancel" },
  "assistantJob.state.accepted": { ru: "Принят", en: "Accepted" },
  "assistantJob.state.queued": { ru: "В очереди", en: "Queued" },
  "assistantJob.state.running": { ru: "В работе", en: "Working" },
  "assistantJob.state.waiting": {
    ru: "Ожидает ввода",
    en: "Waiting for input",
  },
  "assistantJob.state.succeeded": { ru: "Готово", en: "Completed" },
  "assistantJob.state.failed": { ru: "Не выполнен", en: "Failed" },
  "assistantJob.state.cancel_requested": {
    ru: "Останавливается",
    en: "Stopping",
  },
  "assistantJob.state.cancelled": { ru: "Отменён", en: "Cancelled" },
  "assistantJob.state.expired": { ru: "Срок истёк", en: "Expired" },
  // Assistant reply feedback (ADR 0216)
  "assistant.feedback.title": {
    ru: "Как вам этот ответ?",
    en: "How was this answer?",
  },
  "assistant.feedback.up": { ru: "Полезно", en: "Helpful" },
  "assistant.feedback.down": { ru: "Не то", en: "Not right" },
  "assistant.feedback.reasonTitle": {
    ru: "Что пошло не так?",
    en: "What went wrong?",
  },
  "assistant.feedback.reason.wrong_action": {
    ru: "Не то действие",
    en: "Wrong action",
  },
  "assistant.feedback.reason.claimed_but_not_done": {
    ru: "Сказал, что сделал, но не сделал",
    en: "Said it was done, but it was not",
  },
  "assistant.feedback.reason.wrong_data": {
    ru: "Не те данные",
    en: "Wrong data",
  },
  "assistant.feedback.reason.wrong_language": {
    ru: "Не тот язык",
    en: "Wrong language",
  },
  "assistant.feedback.reason.too_slow": {
    ru: "Слишком долго",
    en: "Too slow",
  },
  "assistant.feedback.reason.other": { ru: "Другое", en: "Other" },
  "assistant.feedback.consent": {
    ru: "Отправить этот диалог на разбор",
    en: "Send this exchange for review",
  },
  "assistant.feedback.consentHint": {
    ru: "Текст сообщения и ответа сохраняется после автоматического удаления контактов, номеров и ключей, доступен только команде качества и удаляется через 30 дней. Имена и адреса автоматически не скрываются.",
    en: "The message and the reply are kept after contacts, numbers and keys are removed automatically, are visible only to the quality team and are deleted after 30 days. Names and addresses are not hidden automatically.",
  },
  "assistant.feedback.send": { ru: "Отправить", en: "Send" },
  "assistant.feedback.back": { ru: "Назад", en: "Back" },
  "assistant.feedback.thanks": {
    ru: "Спасибо за отзыв.",
    en: "Thank you for the feedback.",
  },
  "assistant.feedback.change": { ru: "Изменить", en: "Change" },
  "assistant.feedback.error": {
    ru: "Не удалось отправить отзыв.",
    en: "Could not send the feedback.",
  },
  // Proposal review
  "proposal.title": { ru: "Предложение ассистента", en: "Assistant proposal" },
  "proposal.unavailable": {
    ru: "Предложение доступно только в подтверждённой сессии Telegram.",
    en: "The proposal is only available in an authenticated Telegram session.",
  },
  "proposal.apply": { ru: "Применить", en: "Apply" },
  "proposal.reject": { ru: "Отклонить", en: "Reject" },
  "proposal.status.pending": { ru: "Ожидает решения", en: "Pending" },
  "proposal.status.applied": { ru: "Применено", en: "Applied" },
  "proposal.status.rejected": { ru: "Отклонено", en: "Rejected" },
  "proposal.status.stale": { ru: "Условия изменились", en: "Stale" },
  "proposal.status.expired": { ru: "Срок истёк", en: "Expired" },
  "proposal.changes": { ru: "Изменения", en: "Changes" },
  "proposal.summary": {
    ru: "Проверьте изменения перед применением.",
    en: "Review the changes before applying them.",
  },

  // Print
  "print.title": { ru: "Печатная форма", en: "Print form" },
  "print.noForms.title": {
    ru: "Для этого раздела нет форм",
    en: "This section has no print forms",
  },
  "print.noForms.body": {
    ru: "Формы печати задаются в схеме раздела. Попросите ассистента в чате добавить форму.",
    en: "Print forms are part of the section schema. Ask the assistant in the chat to add one.",
  },
  "print.noForms.action": {
    ru: "Спросить ассистента",
    en: "Ask the assistant",
  },
  "print.preview": { ru: "Предпросмотр", en: "Preview" },
  "print.private": { ru: "Приватная печатная форма", en: "Private print form" },
  "print.action": {
    ru: "Печать / Сохранить как PDF",
    en: "Print / Save as PDF",
  },
  "print.showPreview": { ru: "Показать предпросмотр", en: "Show preview" },
  "print.unsupported.title": {
    ru: "Печать здесь недоступна",
    en: "Printing is unavailable here",
  },
  "print.unsupported.body": {
    ru: "Этот экран Telegram не поддерживает печать. Откройте форму во внешнем браузере, чтобы распечатать.",
    en: "This Telegram surface does not support printing. Open the form in an external browser to print.",
  },
  "print.openExternal": { ru: "Открыть в браузере", en: "Open in browser" },
  "print.draftStamp": { ru: "ЧЕРНОВИК", en: "DRAFT" },
  "print.page": { ru: "Стр.", en: "Page" },
  "print.of": { ru: "из", en: "of" },
  "print.notFound": {
    ru: "Эта печатная форма не найдена или недоступна.",
    en: "This print form was not found or is unavailable.",
  },

  // Sections
  "sections.search": { ru: "Поиск раздела", en: "Search sections" },
  "sections.recent": { ru: "Недавние", en: "Recent" },
  "sections.system": { ru: "Системные", en: "System" },
  "sections.mine": { ru: "Мои", en: "Mine" },
  "sections.systemSubtitle": { ru: "Системный раздел", en: "System section" },
  "sections.updated": { ru: "обновлён", en: "updated" },
  "sections.noneMine": {
    ru: "Своих разделов пока нет",
    en: "No sections of your own yet",
  },
  "sections.create": { ru: "Создать свой раздел", en: "Create a section" },
  "sections.rename": { ru: "Переименовать", en: "Rename" },
  "sections.renameTitle": {
    ru: "Новое название раздела",
    en: "New section name",
  },
  "sections.renameHint": {
    ru: "От одного до трёх слов.",
    en: "One to three words.",
  },
  "sections.renameTooLong": {
    ru: "Слишком длинное название: нужно не больше трёх слов.",
    en: "That name is too long: use three words at most.",
  },
  "sections.renameTaken": {
    ru: "Раздел с таким названием уже есть.",
    en: "A section with that name already exists.",
  },
  "sections.renameSave": { ru: "Сохранить", en: "Save" },
  "sections.renameError": {
    ru: "Не удалось переименовать. Попробуйте ещё раз.",
    en: "The section was not renamed. Try again.",
  },
  "sections.actions": { ru: "Действия с разделом", en: "Section actions" },
  "sections.draftBadge": { ru: "Черновик", en: "Draft" },
  "sections.edit": { ru: "Изменить раздел", en: "Edit section" },
  "sections.edit.intro": {
    ru: "Опишите ассистенту в чате, что изменить. Он подготовит черновик, а вы проверите его здесь и опубликуете.",
    en: "Tell the assistant in the chat what to change. It prepares a draft; you review it here and publish.",
  },
  "sections.edit.examples": { ru: "Например", en: "For example" },
  "sections.edit.example1": {
    ru: 'Добавь поле "Поставщик"',
    en: 'Add a "Supplier" field',
  },
  "sections.edit.example2": {
    ru: 'Спрячь поле "Заметка"',
    en: 'Hide the "Note" field',
  },
  "sections.edit.example3": {
    ru: "Отсортируй список по названию",
    en: "Sort the list by name",
  },
  "sections.edit.chat": { ru: "Вернуться в чат", en: "Back to chat" },
  "sections.edit.draft": { ru: "Открыть черновик", en: "Open the draft" },
  "sections.edit.draftStale": {
    ru: "Черновик устарел: раздел изменился после его создания.",
    en: "The draft is out of date: the section changed after it was made.",
  },
  "sections.edit.draftReady": {
    ru: "Для этого раздела есть неопубликованный черновик.",
    en: "This section has an unpublished draft.",
  },
  "sections.found": { ru: "Найдено", en: "Found" },
  "sections.notFound.title": {
    ru: "Раздел не найден",
    en: "No section found",
  },
  "sections.notFound.body": {
    ru: "Проверьте написание или создайте свой раздел — опишите его ассистенту в чате.",
    en: "Check the spelling or create your own section — describe it to the assistant in the chat.",
  },
  "sections.none": { ru: "Раздел не выбран", en: "No section chosen" },
  "sections.firstRun.title": {
    ru: "Начните с раздела",
    en: "Start with a section",
  },
  "sections.firstRun.body": {
    ru: "Раздел — это то, что вы ведёте: напоминания, тренировки, расходы. Выберите системный или создайте свой.",
    en: "A section is what you keep track of: reminders, workouts, expenses. Pick a system one or create your own.",
  },
  "sections.firstRun.create": { ru: "Создать свой", en: "Create my own" },
  "sections.firstRun.choose": { ru: "Выбрать раздел", en: "Choose a section" },
  "count.record.one": { ru: "запись", en: "record" },
  "count.record.few": { ru: "записи", en: "records" },
  "count.record.many": { ru: "записей", en: "records" },
  "count.record.other": { ru: "записи", en: "records" },
  "count.section.one": { ru: "раздел", en: "section" },
  "count.section.few": { ru: "раздела", en: "sections" },
  "count.section.many": { ru: "разделов", en: "sections" },
  "count.section.other": { ru: "раздела", en: "sections" },

  // Profile
  "profile.service": { ru: "Служебное", en: "Service" },
  "profile.settings": { ru: "Настройки", en: "Settings" },
  "profile.timezone": { ru: "Часовой пояс", en: "Time zone" },
  "profile.timezoneSearch": { ru: "Поиск", en: "Search" },
  "profile.now": { ru: "сейчас", en: "now" },
  "profile.language": { ru: "Язык", en: "Language" },
  "profile.languageTitle": {
    ru: "Язык интерфейса",
    en: "Interface language",
  },
  "profile.settingFailed": {
    ru: "Не удалось сохранить настройку. Повторите попытку.",
    en: "The setting could not be saved. Try again.",
  },
  "language.ru": { ru: "Русский", en: "Русский" },
  "language.en": { ru: "English", en: "English" },
  "count.zone.one": { ru: "зона", en: "zone" },
  "count.zone.few": { ru: "зоны", en: "zones" },
  "count.zone.many": { ru: "зон", en: "zones" },
  "count.zone.other": { ru: "зоны", en: "zones" },
  "profile.title": { ru: "Профиль", en: "Profile" },
  "profile.plan": { ru: "Текущий план", en: "Current plan" },
  "profile.quotas": { ru: "Квоты", en: "Quotas" },
  "profile.packs": { ru: "Пакеты кредитов", en: "Credit packs" },
  "profile.orders": { ru: "История платежей", en: "Payments" },
  "profile.invoiceNote": {
    ru: "Инвойс открывает Telegram. Успех показывается только после подтверждённого начисления.",
    en: "Telegram opens the invoice. Success is shown only after a confirmed credit.",
  },
  "profile.upgrade": { ru: "Оплатить Pro", en: "Upgrade to Pro" },
  "profile.upgradePending": {
    ru: "Открываем оплату…",
    en: "Opening the payment sheet…",
  },
  "profile.upgradeChecking": {
    ru: "Проверяем оплату…",
    en: "Confirming the payment…",
  },
  "profile.upgradeFailed": {
    ru: "Не удалось открыть оплату. Попробуйте ещё раз.",
    en: "The payment could not be opened. Please try again.",
  },
  "profile.billingDisabled": {
    ru: "Оплата в этой среде выключена.",
    en: "Payments are switched off in this environment.",
  },
  "profile.usageCommitted": {
    ru: "Подтверждённые операции",
    en: "Committed operations",
  },
  "profile.noPacks": { ru: "Нет активных пакетов", en: "No active packs" },
  "profile.noOrders": { ru: "Платежей пока нет", en: "No payments yet" },

  // Admin
  "admin.title": { ru: "Admin Console", en: "Admin Console" },
  "admin.tabs.label": { ru: "Разделы консоли", en: "Console sections" },
  "admin.tabs.overview": { ru: "Обзор", en: "Overview" },
  "admin.tabs.models": { ru: "Модели", en: "Models & providers" },
  "admin.retry": { ru: "Повторить", en: "Retry" },
  "admin.session.title": { ru: "Сессия истекла", en: "Session expired" },
  "admin.session.body": {
    ru: "Сессия оператора закончилась. Войдите снова, чтобы продолжить.",
    en: "Your operator session has ended. Sign in again to continue.",
  },
  "admin.session.signIn": { ru: "Войти снова", en: "Sign in again" },
  "admin.load.notConfigured": {
    ru: "Источник метрик не настроен: показатели недоступны, пока он не подключён.",
    en: "The metrics source is not configured, so these figures are unavailable until it is connected.",
  },
  "admin.models.remoteImmutable": {
    ru: "Сервер сейчас не позволяет менять этот переключатель.",
    en: "The server does not allow changing this switch right now.",
  },
  "admin.denied.title": { ru: "Нет доступа", en: "Access denied" },
  "admin.denied.load": {
    ru: "У вашей сессии нет права observability.read, поэтому нагрузка и очереди недоступны.",
    en: "Your session lacks the observability.read permission, so load and queues are unavailable.",
  },
  "admin.denied.models": {
    ru: "У вашей сессии нет права observability.read, поэтому данные моделей недоступны.",
    en: "Your session lacks the observability.read permission, so model data is unavailable.",
  },
  "admin.denied.registry": {
    ru: "У вашей сессии нет права model_registry.manage, поэтому реестр провайдеров и переключатели недоступны.",
    en: "Your session lacks the model_registry.manage permission, so the provider registry and switches are unavailable.",
  },
  "admin.denied.inspector": {
    ru: "У вашей сессии нет права assistant.trace.read, поэтому инспектор запросов недоступен.",
    en: "Your session lacks the assistant.trace.read permission, so the job inspector is unavailable.",
  },
  "admin.denied.feedback": {
    ru: "У вашей сессии нет права assistant.feedback.review, поэтому очередь отзывов недоступна.",
    en: "Your session lacks the assistant.feedback.review permission, so the feedback queue is unavailable.",
  },
  "admin.models.registryFailed": {
    ru: "Не удалось загрузить реестр провайдеров.",
    en: "The provider registry could not be loaded.",
  },
  "admin.models.registryLoading": {
    ru: "Загрузка реестра провайдеров",
    en: "Loading the provider registry",
  },
  "admin.inspector.queue.loading": {
    ru: "Загрузка очереди",
    en: "Loading the queue",
  },
  // Job inspector and feedback queue (ADR 0216, 0217)
  "admin.inspector.title": { ru: "Инспектор запросов", en: "Job inspector" },
  "admin.inspector.notice": {
    ru: "Содержимое очищено автоматически (имена и адреса могут остаться). Каждое чтение журналируется. Не копируйте его в чаты и задачи.",
    en: "Content is redacted automatically (names and addresses may remain). Every read is audited. Do not copy it into chats or issues.",
  },
  "admin.inspector.loading": { ru: "Загрузка", en: "Loading" },
  "admin.inspector.failed": {
    ru: "Не удалось загрузить данные.",
    en: "Could not load the data.",
  },
  "admin.inspector.empty": { ru: "Ничего не найдено.", en: "Nothing found." },
  "admin.inspector.filter.state": { ru: "Состояние", en: "State" },
  "admin.inspector.filter.anyState": { ru: "Любое", en: "Any" },
  "admin.inspector.filter.correction": {
    ru: "Только с исправлением",
    en: "Only with a correction",
  },
  "admin.inspector.filter.feedback": {
    ru: "Только с отзывом",
    en: "Only with feedback",
  },
  "admin.inspector.more": { ru: "Показать ещё", en: "Show more" },
  "admin.inspector.open": { ru: "Открыть", en: "Open" },
  "admin.inspector.back": { ru: "К списку", en: "Back to the list" },
  "admin.inspector.noTrace": {
    ru: "Трассировки нет (срок хранения 14 дней или задача старше).",
    en: "No trace (kept 14 days, or the job is older).",
  },
  "admin.inspector.turn": { ru: "Шаг", en: "Turn" },
  "admin.inspector.intent": { ru: "намерение", en: "intent" },
  "admin.inspector.blocks": { ru: "блоки", en: "blocks" },
  "admin.inspector.tools": { ru: "инструменты", en: "tools" },
  "admin.inspector.correction": { ru: "исправление", en: "correction" },
  "admin.inspector.none": { ru: "нет", en: "none" },
  "admin.inspector.snapshot": {
    ru: "Очищенный диалог (отправлен владельцем)",
    en: "Redacted exchange (sent by the owner)",
  },
  "admin.inspector.queue.title": {
    ru: "Очередь отзывов",
    en: "Feedback queue",
  },
  "admin.inspector.queue.status": { ru: "Статус", en: "Status" },
  "admin.inspector.queue.reviewed": { ru: "Просмотрено", en: "Mark reviewed" },
  "admin.inspector.queue.discarded": { ru: "Отклонить", en: "Discard" },
  "admin.inspector.queue.noSnapshot": {
    ru: "Диалог не отправлен: только оценка.",
    en: "No exchange was sent: verdict only.",
  },
  // Observability panels (OBS-008)
  "admin.load.title": {
    ru: "Нагрузка и очереди",
    en: "Load and queues",
  },
  "admin.load.loading": {
    ru: "Загрузка",
    en: "Loading",
  },
  "admin.load.failed": {
    ru: "Не удалось загрузить данные нагрузки.",
    en: "Load data could not be loaded.",
  },
  "admin.load.retry": {
    ru: "Повторить",
    en: "Retry",
  },
  "admin.load.unavailable": {
    ru: "Данные недоступны",
    en: "Data unavailable",
  },
  "admin.load.jobsTotal": {
    ru: "Задач за окно",
    en: "Jobs in window",
  },
  "admin.load.failureRate": {
    ru: "Доля сбоев",
    en: "Failure rate",
  },
  "admin.load.durationP50": {
    ru: "Длительность p50, с",
    en: "Duration p50, s",
  },
  "admin.load.durationP95": {
    ru: "Длительность p95, с",
    en: "Duration p95, s",
  },
  "admin.load.hold": {
    ru: "Удержание очереди",
    en: "Queue hold",
  },
  "admin.load.outboxLag": {
    ru: "Задержка outbox, с",
    en: "Outbox lag, s",
  },
  "admin.load.queues": {
    ru: "Очереди",
    en: "Queues",
  },
  "admin.load.queue": {
    ru: "Очередь",
    en: "Queue",
  },
  "admin.load.ready": {
    ru: "Готово",
    en: "Ready",
  },
  "admin.load.consumers": {
    ru: "Потребители",
    en: "Consumers",
  },
  "admin.load.queuesEmpty": {
    ru: "Очередей нет.",
    en: "No queues.",
  },
  "admin.load.waitingAccepted": {
    ru: "Принято",
    en: "Accepted",
  },
  "admin.load.waitingQueued": {
    ru: "В очереди",
    en: "Queued",
  },
  "admin.load.waitingWaiting": {
    ru: "Ожидают",
    en: "Waiting",
  },
  "admin.load.jobClasses": {
    ru: "Нагрузка по классам задач",
    en: "Load by job class",
  },
  "admin.load.jobClass": {
    ru: "Класс",
    en: "Class",
  },
  "admin.load.busy": {
    ru: "Занято",
    en: "Busy",
  },
  "admin.load.waitP95": {
    ru: "Ожидание p95, с",
    en: "Wait p95, s",
  },
  "admin.load.jobClassesEmpty": {
    ru: "Нет данных по классам.",
    en: "No job class data.",
  },
  "admin.load.vllm": {
    ru: "vLLM",
    en: "vLLM",
  },
  "admin.load.vllmRunning": {
    ru: "Идут",
    en: "Running",
  },
  "admin.load.vllmWaiting": {
    ru: "Ждут",
    en: "Waiting",
  },
  "admin.load.vllmKv": {
    ru: "KV-кэш",
    en: "KV cache",
  },
  "admin.models.title": {
    ru: "Модели и провайдеры",
    en: "Models and providers",
  },
  "admin.models.failed": {
    ru: "Не удалось загрузить данные моделей.",
    en: "Model data could not be loaded.",
  },
  "admin.models.table": {
    ru: "Модели за окно",
    en: "Models in window",
  },
  "admin.models.provider": {
    ru: "Провайдер",
    en: "Provider",
  },
  "admin.models.exposure": {
    ru: "Тип",
    en: "Exposure",
  },
  "admin.models.exposureInternal": {
    ru: "Внутренний",
    en: "Internal",
  },
  "admin.models.exposureExternal": {
    ru: "Внешний",
    en: "External",
  },
  "admin.models.rating": {
    ru: "Рейтинг",
    en: "Rating",
  },
  "admin.models.breaker": {
    ru: "Выключатель",
    en: "Breaker",
  },
  "admin.models.breakerClosed": {
    ru: "Работает",
    en: "Closed",
  },
  "admin.models.breakerOpen": {
    ru: "Отключён",
    en: "Open",
  },
  "admin.models.breakerHalfOpen": {
    ru: "Проба",
    en: "Probing",
  },
  "admin.models.providersTable": {
    ru: "Провайдеры",
    en: "Providers",
  },
  "admin.models.unrated": {
    ru: "нет оценки",
    en: "unrated",
  },
  "admin.models.model": {
    ru: "Модель",
    en: "Model",
  },
  "admin.models.calls": {
    ru: "Вызовы",
    en: "Calls",
  },
  "admin.models.errors": {
    ru: "Ошибки",
    en: "Errors",
  },
  "admin.models.latencyP50": {
    ru: "Задержка p50, мс",
    en: "Latency p50, ms",
  },
  "admin.models.latencyP95": {
    ru: "Задержка p95, мс",
    en: "Latency p95, ms",
  },
  "admin.models.tokens": {
    ru: "Токены вх/исх",
    en: "Tokens in/out",
  },
  "admin.models.mini.p95": {
    ru: "p95 мс",
    en: "p95 ms",
  },
  "admin.models.mini.usd": {
    ru: "USD",
    en: "USD",
  },
  "admin.models.spend": {
    ru: "Расход, USD",
    en: "Spend, USD",
  },
  "admin.models.empty": {
    ru: "Вызовов моделей нет.",
    en: "No model calls.",
  },
  "admin.models.topErrors": {
    ru: "Частые исходы с ошибками",
    en: "Top error outcomes",
  },
  "admin.models.outcome": {
    ru: "Исход",
    en: "Outcome",
  },
  "admin.models.errorClass": {
    ru: "Класс ошибки",
    en: "Error class",
  },
  "admin.models.count": {
    ru: "Число",
    en: "Count",
  },
  "admin.models.errorsEmpty": {
    ru: "Ошибок нет.",
    en: "No errors.",
  },
  "admin.models.switches": {
    ru: "Переключатели провайдеров",
    en: "Provider switches",
  },
  "admin.models.providerSwitch": {
    ru: "Провайдер включён",
    en: "Provider enabled",
  },
  "admin.models.remoteSwitch": {
    ru: "Отправка пользовательского контента внешним провайдерам",
    en: "Remote user content",
  },
  "admin.models.abilityBriefSwitch": {
    ru: "Внешний ИИ для интервью и создания способностей",
    en: "Remote AI for interviews and ability design",
  },
  "admin.models.abilityBriefHelp": {
    ru: "Внешним провайдерам уходит только описание структуры способности, которое дал владелец: без записей, напоминаний и истории. Перед отправкой текст автоматически проверяется на персональные данные, а получают его только одобренные провайдеры с нулевым хранением.",
    en: "Only the owner's description of the ability structure is sent to remote providers: no records, reminders or history. The text is screened for personal data automatically first, and only approved zero-retention providers receive it.",
  },
  "admin.models.confirmAbilityBriefOn": {
    ru: "Разрешить внешний ИИ для интервью и создания способностей?",
    en: "Allow remote AI for interviews and ability design?",
  },
  "admin.models.confirmAbilityBriefOff": {
    ru: "Запретить внешний ИИ для интервью и создания способностей?",
    en: "Stop using remote AI for interviews and ability design?",
  },
  "admin.models.reason": {
    ru: "Причина",
    en: "Reason",
  },
  "admin.models.confirm": {
    ru: "Подтвердить",
    en: "Confirm",
  },
  "admin.models.cancel": {
    ru: "Отмена",
    en: "Cancel",
  },
  "admin.models.confirmEnable": {
    ru: "Включить провайдера?",
    en: "Enable the provider?",
  },
  "admin.models.confirmDisable": {
    ru: "Отключить провайдера?",
    en: "Disable the provider?",
  },
  "admin.models.confirmRemoteOn": {
    ru: "Разрешить отправку пользовательского контента внешним провайдерам?",
    en: "Allow sending user content to remote providers?",
  },
  "admin.models.confirmRemoteOff": {
    ru: "Запретить отправку пользовательского контента внешним провайдерам?",
    en: "Stop sending user content to remote providers?",
  },
  "admin.models.switchDenied": {
    ru: "Нет прав на управление переключателями.",
    en: "You are not allowed to change these switches.",
  },
  "admin.models.switchFailed": {
    ru: "Не удалось применить изменение.",
    en: "The change could not be applied.",
  },
  "admin.models.stepUpUnavailable": {
    ru: "Для этого действия нужно дополнительное подтверждение, оно пока недоступно.",
    en: "This action needs step-up confirmation, which is not available yet.",
  },
  "admin.models.reason.incident": {
    ru: "Инцидент",
    en: "Incident",
  },
  "admin.models.reason.quality_regression": {
    ru: "Падение качества",
    en: "Quality regression",
  },
  "admin.models.reason.cost": {
    ru: "Стоимость",
    en: "Cost",
  },
  "admin.models.reason.maintenance": {
    ru: "Обслуживание",
    en: "Maintenance",
  },
  "admin.models.reason.rollout": {
    ru: "Раскатка",
    en: "Rollout",
  },
  "admin.enter": { ru: "Открыть консоль", en: "Open console" },
  "admin.signIn": { ru: "Войти как оператор", en: "Sign in as operator" },
  "admin.noAccess": {
    ru: "Для этой учётной записи операторский доступ не назначен.",
    en: "Operator access is not assigned to this account.",
  },
  "admin.loadFailed": {
    ru: "Не удалось загрузить состояние платформы.",
    en: "Platform status could not be loaded.",
  },
  "admin.browserNote": {
    ru: "Вход выполняется через системный браузер, не через WebView.",
    en: "Sign-in happens in the system browser, not in the WebView.",
  },
  "admin.handoffWaiting": {
    ru: "Завершите вход в браузере, затем вернитесь в Telegram.",
    en: "Finish signing in in the browser, then return to Telegram.",
  },
  "admin.handoffFailed": {
    ru: "Вход не завершён: время вышло или попытка отклонена. Попробуйте ещё раз.",
    en: "Sign-in was not completed: it timed out or was rejected. Try again.",
  },
  "operations.title": { ru: "Операции", en: "Operations" },
  "operations.telegram": { ru: "Telegram", en: "Telegram" },
  "operations.incidents": { ru: "Инциденты", en: "Incidents" },
  "operations.outboxLag": { ru: "Задержка outbox", en: "Outbox lag" },
  "operations.activeJobs": { ru: "Активные задачи", en: "Active jobs" },
  "operations.release": { ru: "Версия", en: "Release" },

  // Widgets
  "widget.records": { ru: "Записи", en: "Records" },
  "widget.details": { ru: "Детали", en: "Details" },
  "widget.selectRecord": {
    ru: "Выберите запись, чтобы посмотреть поля.",
    en: "Select a record to inspect its fields.",
  },
  "widget.noRecordSelected": {
    ru: "Запись не выбрана.",
    en: "No record selected.",
  },
  "widget.recordForm": { ru: "Форма записи", en: "Record form" },
  "widget.previewLocal": {
    ru: "Изменения в предпросмотре остаются локальными.",
    en: "Preview changes stay local.",
  },
  "widget.serverConfirmed": {
    ru: "Изменения подтверждаются сервером.",
    en: "Changes are confirmed by the server.",
  },
  "widget.recordCount": { ru: "записей", en: "records" },
  "widget.unsupportedElement": {
    ru: "Неподдерживаемый элемент",
    en: "Unsupported element",
  },
  "widget.unsupportedWidget": {
    ru: "Неподдерживаемый widget",
    en: "Unsupported widget",
  },
  "widget.open": { ru: "Открыть", en: "Open" },
  "widget.metric": { ru: "Показатель", en: "Metric" },
  "controls.more": { ru: "Ещё действия", en: "More actions" },
  "controls.create.title": { ru: "Новая запись", en: "New record" },
  "list.group.none": { ru: "Без значения", en: "No value" },
  "list.group.yes": { ru: "Да", en: "Yes" },
  "list.group.no": { ru: "Нет", en: "No" },

  // Table

  // Field controls
  "field.chooseDate": { ru: "Выберите дату", en: "Choose a date" },
  "field.chooseTime": { ru: "Выберите время", en: "Choose a time" },
  "field.choose": { ru: "Выберите", en: "Choose" },
  "field.remove": { ru: "Убрать", en: "Remove" },
  "field.empty": { ru: "—", en: "—" },
} as const;

export type MessageKey = keyof typeof messages;
